import React, { useState } from "react";
import {
  StarIcon,
  LocationIcon,
  CalendarIcon,
} from "../../../images/icons/icons";
import { NoImageOffer } from "../../../exports/Images";
import { useSelector } from "react-redux";
import ReactVortoxSpinner from "../../common-components/ReactVortoxSpinner";

const AllGymOffersFilter = () => {
  const [activeFilter, setActiveFilter] = useState("All Offers");
  const { ongoingOffers, ongoingOffersLoading } = useSelector((state) => state.plans);

  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  // Get unique campaign names from API data
  const uniqueCampaigns = [...new Set(ongoingOffers?.data?.map(offer => offer?.campaign_name))];
  const filterOptions = uniqueCampaigns?.map((campaign) => {
    return normalizeText(campaign);
  }) || [];

  const filterTabs = ["All Offers", ...filterOptions];

  const getFilteredOffers = () => {
    if (!ongoingOffers?.data) return [];
    if (activeFilter === "All Offers") {
      return ongoingOffers.data;
    }
    // Filter offers based on the selected campaign
    return ongoingOffers.data.filter(offer => {
      const offerCampaignNormalized = normalizeText(offer.campaign_name);
      return offerCampaignNormalized === activeFilter;
    });
  };

  // Show loading spinner while fetching data
  if (ongoingOffersLoading) {
    return (
      <div className="all-gym-offer-filter-section" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <ReactVortoxSpinner />
      </div>
    );
  }

  // Show empty state if no data
  if (!ongoingOffers?.data || ongoingOffers.data.length === 0) {
    return (
      <div className="all-gym-offer-filter-section" style={{ display: 'flex', justifyContent: 'center', alignItems: 'start', minHeight: '400px' }}>
        <p style={{ fontSize: '18px', color: '#6b7280' }}>No offers available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="all-gym-offer-filter-section">
      {/* <div className="all-gym-offer-filter-header">
        <h2 className="all-gym-offer-filter-title">All Gym Offers</h2>
        <p className="all-gym-offer-filter-description">
          Manage offers across all gym locations. Click 'View Offer Details' to
          access the member-facing offer page.
        </p>
      </div> */}

      <div className="all-gym-offer-filter-tabs">
        {filterTabs.map((tab, index) => (
          <button
            key={index}
            className={`all-gym-offer-filter-tab ${activeFilter === tab ? "all-gym-offer-filter-tab-active" : ""
              }`}
            onClick={() => setActiveFilter(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="all-gym-offer-cards-section">
        {getFilteredOffers().map((offer) => {
          // Calculate discount percentage
          const actualPrice = parseFloat(offer.actual_price);
          const offerPrice = parseFloat(offer.offer_price);
          const discountPercent = Math.round(((actualPrice - offerPrice) / actualPrice) * 100);

          // Detect if image_url is a video based on file extension
          const isVideo = offer.image_url && (
            offer.image_url.includes('.mp4') ||
            offer.image_url.includes('.mov') ||
            offer.image_url.includes('.avi') ||
            offer.image_url.includes('.webm')
          );

          return (
            <div key={offer.uid} className="all-gym-offer-card">
              <div className="all-gym-offer-card-header">
                {/* Video Background */}
                {isVideo ? (
                  <video
                    className="all-gym-offer-card-background-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={NoImageOffer}
                    onError={(e) => {
                      // If video fails to load, fallback to placeholder
                      e.target.style.display = 'none';
                      const fallbackDiv = e.target.nextElementSibling;
                      if (fallbackDiv) {
                        fallbackDiv.style.display = 'block';
                      }
                    }}
                  >
                    <source src={offer.image_url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : null}

                {/* Image Background - Show only if not video or as fallback */}
                <div
                  className="all-gym-offer-card-background-image"
                  style={{
                    background: !isVideo && offer.image_url ? `url(${offer.image_url})` : `url(${NoImageOffer})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    display: isVideo ? 'none' : 'block'
                  }}
                />

                {/* Content Overlay */}
                <div className="all-gym-offer-card-content-overlay">
                  <div className="all-gym-offer-card-badges">
                    <span className="all-gym-offer-card-badge">{discountPercent}% OFF</span>
                    {offer.status && (
                      <span className="all-gym-offer-card-status">
                        {offer.status}
                      </span>
                    )}
                  </div>
                  <div className="all-gym-offer-card-title-section">
                    <h3 className="all-gym-offer-card-main-title">{normalizeText(offer.title)}</h3>
                  </div>
                </div>
              </div>

              <div className="all-gym-offer-card-content">
                <div className="all-gym-offer-card-info">
                  <div className="d-flex items-center justify-content-between">
                    <div>
                      <h4 className="all-gym-offer-card-subtitle">
                        {normalizeText(offer.campaign_name)}
                      </h4>
                      <p className="all-gym-offer-card-description">
                        {offer.description}
                      </p>
                    </div>

                    <div className="all-gym-offer-card-rating">
                      <StarIcon color="#fbbf24" />
                      <span className="all-gym-offer-rating-value">
                        4.8
                      </span>
                      <span className="all-gym-offer-rating-text">rating</span>
                    </div>
                  </div>

                  <div className="all-gym-offer-benefits">
                    <h5 className="all-gym-offer-benefits-title">Benefits:</h5>
                    <ul className="all-gym-offer-benefits-list">
                      {offer.benefits.map((benefit, index) => (
                        <li key={index} className="all-gym-offer-benefit-item">
                          <span className="all-gym-offer-benefit-dot">•</span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="all-gym-offer-locations">
                    <div className="all-gym-offer-locations-header">
                      <LocationIcon color="#ef4444" />
                      <span className="all-gym-offer-locations-title">
                        Available at ({offer.gyms.length} gyms):
                      </span>
                    </div>
                    <div className="all-gym-offer-gyms-list">
                      {offer.gyms.map((gym, index) => (
                        <div key={index} className="all-gym-offer-gym-item">
                          <div className="all-gym-offer-gym-left">
                            <span className="all-gym-offer-gym-indicator">•</span>
                            <span className="all-gym-offer-gym-name">
                              {gym.gym_name}
                            </span>
                          </div>
                          <span className="all-gym-offer-gym-location">
                            {gym.gym_category_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="all-gym-offer-validity">
                    <CalendarIcon color="#6b7280" />
                    <span className="all-gym-offer-validity-text">
                      Valid until:
                    </span>
                    <span className="all-gym-offer-validity-date">
                      {new Date(offer.valid_to).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="all-gym-offer-card-footer">
                  <div className="all-gym-offer-pricing">
                    <div className="all-gym-offer-price-main">
                      <span className="all-gym-offer-price">₹{offer.offer_price}</span>
                      <span className="all-gym-offer-original-price">
                        ₹{offer.actual_price}
                      </span>
                    </div>
                    <span className="all-gym-offer-per-month">per month</span>
                  </div>
                  <button
                    className="all-gym-offer-view-details-btn"
                    onClick={() => {
                      if (offer?.landing_page_url) {
                        // Ensure URL has protocol
                        const url = offer?.landing_page_url?.startsWith('http')
                          ? offer.landing_page_url
                          : `https://${offer?.landing_page_url}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    disabled={!offer.landing_page_url}
                    style={{
                      opacity: offer.landing_page_url ? 1 : 0.5,
                      cursor: offer.landing_page_url ? 'pointer' : 'not-allowed'
                    }}
                  >
                    View Offer Details
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AllGymOffersFilter;
